import modelBaseV2 from "../model-base-v2.model";

/**
 * Incentive Report Model
 *
 * API wrappers for the FFIS report routes (the 6 APPROVER routes of api/incentive/report).
 * Mapped to menu WM021105 "Reports" and 403 until that menu is granted, per country.
 *
 * Country and user are NOT sent from here - the backend reads them from the session.
 *
 * ============================================================================================
 * ⚠️ THESE READ ANY EMPLOYEE. THAT IS THE POINT, AND THE REASON FOR THE MENU BOUNDARY.
 * ============================================================================================
 * Unlike the self-service procedures (IncentiveMy*Get, subject taken from the token with no
 * userId parameter at all), these take a userId and will return whoever is asked for. That is
 * what an approver needs and is why they sit behind WM021105 rather than a promoter menu.
 *
 * So do not reuse getEmployeeBreakdown() to build a supervisor screen. It is restricted only by
 * the menu, and a supervisor holding that menu could read the whole country's pay.
 *
 * ============================================================================================
 * WHICH RUN THESE FIGURES COME FROM
 * ============================================================================================
 * Omit calcRunId and the server resolves the scope's run itself. Since 2026-09-18 it PREFERS a
 * non-nightly run and only falls back to a nightly one when no payout run exists for the period,
 * because the unattended batch runs every night and its inputs were never reviewed by anyone.
 *
 * ⚠️ READ runAuthority OFF THE SUMMARY AND SHOW IT. It reads GATED / PROVISIONAL / SIMULATION.
 * An approver reading PROVISIONAL figures is looking at last night's snapshot, not at the payout
 * they are about to sign - and the two can legitimately differ.
 *
 * @author FFIS
 */
const IncentiveReportModel = {

  /**
   * Three result sets: the run header (with runSelection and runAuthority), a per-position
   * summary, and a zero-reason breakdown.
   *
   * The zero-reason set is worth surfacing rather than hiding: a period where 400 people earned
   * nothing is either a bad month or a broken segment mapping, and only the reason codes tell
   * you which.
   */
  getSummary: (scope) => {
    const queryString = modelBaseV2.toQueryString(scope);
    return modelBaseV2.get(`/api/incentive/report/summary?${queryString}`);
  },

  /** One row per person - the R-01..R-10 columns of the approval workbook. */
  getEmployees: (filters) => {
    const queryString = modelBaseV2.toQueryString(filters);
    return modelBaseV2.get(`/api/incentive/report/employee?${queryString}`);
  },

  /** The wide detail view, optionally narrowed to one position. */
  getEmployeeDetail: (filters) => {
    const queryString = modelBaseV2.toQueryString(filters);
    return modelBaseV2.get(`/api/incentive/report/employee/detail?${queryString}`);
  },

  /**
   * One person's component trail - "the feature that replaces asking the analyst".
   *
   * Each component carries its inputs, the rate and multiplier applied, the tier it landed in,
   * and parameterTrace: a human-readable derivation the primitives write as they calculate. That
   * string is the answer to "why is my figure this" without re-running anything.
   */
  getEmployeeBreakdown: (userId, scope) => {
    const queryString = modelBaseV2.toQueryString(scope);
    return modelBaseV2.get(
      `/api/incentive/report/employee/${userId}/breakdown?${queryString}`);
  },

  /**
   * Store roll-up.
   *
   * ⚠️ TARGET AND ACHIEVEMENT COME BACK NULL. IncentiveResultStoreSave is called by no
   * calculator - only the EC pipeline exists, and store-level results belong to the leader and
   * manager pipelines that do not - so this falls back to a DERIVED aggregate over employee
   * results. It sums what was paid; it cannot show what the store was aiming at. Do not render
   * an empty target as 0.
   */
  getStores: (scope) => {
    const queryString = modelBaseV2.toQueryString(scope);
    return modelBaseV2.get(`/api/incentive/report/store?${queryString}`);
  },

  /**
   * Export rows for a template. exportType is APPROVAL or PAYROLL - nothing else is accepted.
   *
   * ⚠️ THIS RETURNS JSON, NOT A FILE. The server sends rows plus a caveat; turning them into a
   * workbook is the page's job. It is not modelBaseV2.getDownload().
   *
   * ⚠️ PAYROLL REFUSES without an APPROVED approval for the run - money must not leave on
   * nobody's authority. APPROVAL does not require one, because that sheet IS what gets approved
   * and demanding approval first would be circular.
   *
   * A refusal comes back as a refusal (HTTP 400 with a message), never as an export with no
   * rows. That is deliberate: an empty file is something somebody ships.
   *
   * ⚠️ The ACCOUNT column is exported EMPTY. It has no source anywhere in FFIS - not on the
   * result, not on the attribute table, not in any upload - and the column exists only so the
   * template's shape is right. The response repeats this in its caveat. Show the caveat.
   */
  exportRows: (exportType, scope) => {
    return modelBaseV2.post(`/api/incentive/report/export/${exportType}`, { body: scope });
  },
};

export default IncentiveReportModel;
